/**
 * @fileoverview Recording management hooks for handling device recording states and paths
 *
 * This file provides custom React hooks for managing recording functionality across multiple devices:
 *
 * useRecording():
 * A comprehensive hook that manages the recording state of multiple devices.
 *
 * It provides:
 * - Device selection state management
 * - Recording status tracking
 * - Batch operations for starting/stopping recordings
 * - Confirmation dialogs for recording operations
 * - Change management between initial and current recording states
 *
 * Key features:
 * - Maintains a map of device recording selections
 * - Tracks currently recording devices
 * - Provides batch stop functionality with confirmation
 * - Handles selective recording updates with change detection
 *
 * useRecordingPath():
 * A utility hook that retrieves the recording storage path from the Electron app context.
 * Only returns meaningful values in Electron environment, otherwise returns 'NA'.
 *
 * @returns {Object} useRecording
 *   - sources: Array of all available devices
 *   - recordingDevices: Array of currently recording devices
 *   - initialDeviceSelectionMap: Initial recording state map
 *   - deviceSelectionMap: Current recording state map
 *   - setDeviceSelectionMap: Function to update recording selections
 *   - stopRecording: Function to stop all recordings with confirmation
 *   - saveChanges: Function to apply recording state changes
 *
 * @returns {string} useRecordingPath
 *   - Returns the current recording path or 'NA' if not in Electron
 */

import type { Room } from '@studio/models/room';
import { broker } from '#~/agent/broker.js';
import { useGetMachineAgentId } from '#~/agent/components/bridge/hooks.js';
import { useAgents } from '#~/agent/hooks.js';
// Gating (spec §6): a device gated by transitory/INVALID auth must not be a
// recordable / routable-to-production source. Per `reference-rules-in-comments`.
import { applyVideoGating, useGatedVideoIds } from '#~/room/use-gating.js';
import { fieldKeyedAgentsEqual, idNameStatusPick } from '#~/agent/provider/use-agents-field-eq.js';
import { useAuth0 } from '#~/auth/index.js';
import { ElectronAPI } from '#~/electron/index.js';
import { isElectronApp } from '#~/electron/utils.js';
import { useMultiDeviceStateQuery } from '#~/statequery/hook.js';
import { DeviceEgressSelector } from '#~/statequery/selectors/device-egress.js';
import { DataSTag, Sel, STagNew } from '#~/stag/index.js';
import { usePrompt } from '@studio/design-system/prompt';
import { StatusEgressState } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { useCallback, useMemo, useState } from 'react';
import { useRecordingDiskSpace } from '../use-recording-disk-space.js';
import { locateProxy } from '#~/agent/utils.js';
import { getCodecWarning } from '../components/codec-warning.js';
import { saveRecordingPath, setDeviceStatus } from '../commands/index.js';

export const useRecording = (roomId?: T_Maybe<Room['id']>) => {
  const allDeviceMap = useMultiDeviceStateQuery(DeviceEgressSelector);
  const { agentId: machineAgentId } = useGetMachineAgentId();
  const agents = useAgents(
    (a) => a.map((a) => ({ id: a.id, name: a.name, status: a.status })),
    (left, right) => fieldKeyedAgentsEqual(left, right, idNameStatusPick)
  );
  const prompt = usePrompt();
  const { allowed: diskNoticesEnabled, blockedReasonByDevice } = useRecordingDiskSpace(roomId);
  // Gating §6 — recording + routing-to-production capability. Drop gated
  // devices (committed-but-INVALID auth) from the source map so they cannot
  // be recorded or routed. Placeholders are never in the state-sync map.
  const gatedIds = useGatedVideoIds();

  // Depend on STABLE inputs. `agents` comes from `useAgents` with no equality
  // fn, so it is a fresh array on every depot tick; listing it here made this
  // memo recompute constantly. That was harmless while the body returned
  // `allDeviceMap` itself, but the §6 gating filter started returning a NEW object
  // per recompute, which React Query reads as changed options -> observer
  // re-subscribe -> re-render -> recompute. Measured 2.2 -> 0.19 cores.
  // `gatedIds` is already stable (useGatedVideoIds memoises over a `shallow`
  // zustand selector), so depending on it directly is safe — the loop came from
  // `agents`, and `applyVideoGating` preserves identity when nothing is gated.
  // Per `reference-rules-in-comments` (ai.knowledge/global).
  const isMachineAgentOffline =
    agents.find((agent) => agent.id === machineAgentId)?.status === 'offline';
  const deviceMap = useMemo(() => {
    if (isMachineAgentOffline) {
      return {};
    }

    return applyVideoGating(allDeviceMap, gatedIds) as typeof allDeviceMap;
  }, [allDeviceMap, isMachineAgentOffline, gatedIds]);

  const initialDeviceSelectionMap: Record<string, boolean> = useMemo(
    () =>
      Object.values(deviceMap).reduce(
        (acc, source) => ({
          ...acc,
          [source.id]:
            source.egress?.recordingStatus?.state === StatusEgressState.EGRESS_STATE_ACTIVE
        }),
        {}
      ),
    [deviceMap]
  );

  const [deviceSelectionMap, setDeviceSelectionMap] =
    useState<Record<string, boolean>>(initialDeviceSelectionMap);

  const recordingDevices = useMemo(() => {
    return Object.values(deviceMap).filter(
      (v) => v.egress?.recordingStatus?.state === StatusEgressState.EGRESS_STATE_ACTIVE
    );
  }, [deviceMap]);

  const stopRecording = useCallback(
    async (cb: T_Maybe<() => void> = undefined) => {
      prompt.confirm({
        // STag on the prompt body and its confirm button so HIL teardown can
        // find and answer this prompt without getByRole('dialog') + copy
        // matching. Per rule: playwright-stag-selectors-only (ai.knowledge/platform).
        ...DataSTag(Sel.ProductionRecording, 'stop_prompt'),
        title: 'Stop Recording',
        message: 'Are you sure you want to stop recording? This will stop recording all sources. ',
        okay: {
          ...STagNew(Sel.ProductionRecording, 'stop_prompt_confirm'),
          variant: 'danger',
          label: 'Stop Recording'
        },
        onConfirm(answer) {
          if (answer) {
            Promise.all(
              Object.values(deviceMap).map((source) => {
                setDeviceStatus(source.id, true, { record: false });
              })
            );

            cb?.();
          }
        }
      });
    },
    [deviceMap]
  );

  const saveChanges = useCallback(() => {
    // Get changes from initialDeviceSelectionMap and deviceSelectionMap.
    const changedDevices = Object.values(deviceMap).filter(
      (v) => initialDeviceSelectionMap[v.id] !== deviceSelectionMap[v.id]
    );

    const makeChanges = () => {
      Promise.all(
        changedDevices.map((source) => {
          const record = deviceSelectionMap[source.id] ?? false;
          // Recheck at confirmation time: disk state can change while the prompt is open.
          if (
            record &&
            diskNoticesEnabled &&
            locateProxy(source.id)?.state.status?.encoder?.diskRecordingBlocked === true
          )
            return;
          // Stale H.265 selections must not reach the encoder start path.
          if (
            record &&
            source.type === 'video' &&
            getCodecWarning('recording', source.videoCodec)
          ) {
            return;
          }
          setDeviceStatus(source.id, false, { record });
        })
      );
    };

    if (recordingDevices.length > 0) {
      prompt.confirm({
        // Tagged too: HIL teardown throws on an unexpected production prompt,
        // and it can only name this one if this one is addressable.
        ...DataSTag(Sel.ProductionRecording, 'update_prompt'),
        title: 'Update Recordings',
        okay: {
          ...STagNew(Sel.ProductionRecording, 'update_prompt_confirm'),
          variant: 'danger',
          label: 'Update Recordings'
        },
        message: (
          <div className='px-16 font-regular'>
            <p>Are you sure you want to update the following recording settings?</p>
            <p className='my-20'>
              {changedDevices.map((source) => (
                <div key={source.id}>
                  <span>Recording will</span>{' '}
                  <span>{deviceSelectionMap[source.id] ? 'start' : 'stop'}</span> <span>for</span>{' '}
                  <span className='font-demibold'>{source.name}</span>.
                </div>
              ))}
            </p>
            <p>Recordings already in progress will not be interrupted.</p>
          </div>
        ),
        onConfirm(answer) {
          if (answer) {
            makeChanges();
          }
        }
      });
    } else {
      makeChanges();
    }
  }, [deviceSelectionMap, deviceMap, diskNoticesEnabled]);

  return {
    sources: Object.values(deviceMap),
    blockedReasonByDevice,
    recordingDevices,
    initialDeviceSelectionMap,
    deviceSelectionMap,
    setDeviceSelectionMap,
    stopRecording,
    saveChanges
  };
};

export const useRecordingPath = () => {
  const { agentId: machineAgentId } = useGetMachineAgentId();
  const { getAccessTokenSilently } = useAuth0();

  const getDefaultRecordingPath = async () => {
    if (!isElectronApp()) {
      return 'NA';
    }
    const data = await ElectronAPI.getRecordingPath();
    return data;
  };

  const savePath = async (recordingPath: string) => {
    if (machineAgentId) {
      saveRecordingPath(machineAgentId, recordingPath);
    } else {
      console.error('machineAgentId is undefined');
    }
  };

  const getDirFromState = async () => {
    if (!machineAgentId) {
      console.error('machineAgentId is undefined');
      return undefined;
    }
    const proxy = broker.acquire(machineAgentId);
    if (!proxy) {
      throw new Error('PW-79B');
    }
    const path = proxy.state.control?.studio?.recordingDirectory;
    return path;
  };

  const saveToPersistentStorage = async () => {
    if (!machineAgentId) {
      console.error('machineAgentId is undefined');
      return;
    }
    const token = await getAccessTokenSilently();
    const proxy = broker.acquire(machineAgentId);
    if (!proxy) {
      throw new Error('PW-79B');
    }
    proxy.save(token);
  };

  return {
    savePath,
    getDirFromState,
    saveToPersistentStorage,
    getDefaultRecordingPath
  };
};
